/**
 * FreeTextReviewTab — trainer manual grading of free-text answers inside the
 * online assessment detail dialog. Per attempt: read responses, award points
 * (capped at question points), optional remarks; saving recomputes the score.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

export default function FreeTextReviewTab({ assessmentId, onGraded }) {
  const { get, post } = useApi();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});          // {attemptId: {qid: {points, remarks}}}
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    get(`/online-assessments/${assessmentId}/reviews`)
      .then((d) => {
        setData(d);
        const init = {};
        (d.reviews || []).forEach((r) => {
          init[r.attempt_id] = {};
          r.items.forEach((it) => {
            init[r.attempt_id][it.question_id] = {
              points: it.points_awarded ?? '',
              remarks: it.remarks || '',
            };
          });
        });
        setDrafts(init);
      })
      .catch(() => toast.error('Failed to load reviews'));
  }, [get, assessmentId]);

  useEffect(load, [load]);

  const setDraft = (aid, qid, patch) =>
    setDrafts((d) => ({ ...d, [aid]: { ...d[aid], [qid]: { ...d[aid][qid], ...patch } } }));

  const saveAttempt = async (review) => {
    const d = drafts[review.attempt_id] || {};
    const grades = review.items
      .filter((it) => d[it.question_id]?.points !== '' && d[it.question_id]?.points != null)
      .map((it) => ({
        question_id: it.question_id,
        points_awarded: Number(d[it.question_id].points),
        remarks: (d[it.question_id].remarks || '').trim() || null,
      }));
    if (!grades.length) { toast.error('Enter points for at least one answer'); return; }
    setSaving(review.attempt_id);
    try {
      const res = await post(
        `/online-assessments/${assessmentId}/attempts/${review.attempt_id}/grade`,
        { grades });
      toast.success(`Graded — new score ${res.score_pct}%`);
      load();
      onGraded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grading failed');
    } finally { setSaving(null); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data.has_free_text) {
    return <p className="text-sm text-muted-foreground p-2" data-testid="review-no-freetext">
      This assessment has no free-text questions.
    </p>;
  }
  if (!data.reviews.length) {
    return <p className="text-sm text-muted-foreground p-2" data-testid="review-empty">
      No written answers submitted yet.
    </p>;
  }

  return (
    <div className="space-y-4" data-testid="freetext-review-tab">
      {data.reviews.map((r) => (
        <Card key={r.attempt_id} className="p-4" data-testid={`review-attempt-${r.attempt_id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-medium text-sm">{r.trainee_name}</p>
              <p className="text-xs text-muted-foreground">
                Attempt {r.attempt_no} · {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ''} · current score {r.score_pct}%
              </p>
            </div>
            {r.pending_review_count > 0
              ? <Badge className="bg-amber-100 text-amber-800 border-0" data-testid={`pending-badge-${r.attempt_id}`}>
                  {r.pending_review_count} pending
                </Badge>
              : <Badge className="bg-emerald-100 text-emerald-800 border-0">Reviewed</Badge>}
          </div>

          <div className="mt-3 space-y-3">
            {r.items.map((it) => (
              <div key={it.question_id} className="rounded-md border p-3"
                   data-testid={`review-item-${r.attempt_id}-${it.question_id}`}>
                <p className="text-sm font-medium">{it.question_text}
                  <span className="ml-2 text-xs text-muted-foreground">({it.points} pt{it.points === 1 ? '' : 's'})</span>
                </p>
                <p className="mt-1.5 text-sm whitespace-pre-wrap rounded bg-muted/40 p-2"
                   data-testid={`review-answer-${r.attempt_id}-${it.question_id}`}>
                  {it.text_answer}
                </p>
                <div className="mt-2 flex items-start gap-3 flex-wrap">
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">Marks (0–{it.points})</p>
                    <Input type="number" min={0} max={it.points} step="0.5" className="w-24 h-8"
                           value={drafts[r.attempt_id]?.[it.question_id]?.points ?? ''}
                           onChange={(e) => setDraft(r.attempt_id, it.question_id, { points: e.target.value })}
                           data-testid={`grade-points-${r.attempt_id}-${it.question_id}`} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[11px] text-muted-foreground mb-1">Remarks (optional)</p>
                    <Textarea rows={1} className="min-h-[32px]"
                              placeholder="Feedback for the trainee…"
                              value={drafts[r.attempt_id]?.[it.question_id]?.remarks ?? ''}
                              onChange={(e) => setDraft(r.attempt_id, it.question_id, { remarks: e.target.value })}
                              data-testid={`grade-remarks-${r.attempt_id}-${it.question_id}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={() => saveAttempt(r)} disabled={saving === r.attempt_id}
                    data-testid={`save-grades-${r.attempt_id}`}>
              {saving === r.attempt_id ? 'Saving…' : 'Save grades'}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
